import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { WifiOff, CloudOff, RefreshCw, ChevronDown, Power, RotateCw, HardDrive } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { useNativeOnlineStatus } from '@/lib/hooks/useNativeOnlineStatus'
import { useIsInteractionBlocked } from '@/lib/hooks/useIsInteractionBlocked'
import { useRelativeTime } from '@/lib/hooks/useRelativeTime'
import { useOfflineBannerStore } from '@/store/useOfflineBannerStore'
import { useOfflineModeStore } from '@/store/useOfflineModeStore'
import { usePrefetchStore } from '@/store/usePrefetchStore'
import { getPersistedLastCompletedAt } from '@/lib/prefetch/prefetchIssues'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { getTopBannerOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

type OfflineBannerProps = {
  bannerIndex?: number
}

export default function OfflineBanner({ bannerIndex = 0 }: OfflineBannerProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isOnline = useNativeOnlineStatus()
  // A modal layer takes pointer input away from everything outside its
  // portal, so these buttons would render at full strength and do nothing
  // when tapped. Offering a dead control is worse than offering none — the
  // STATE stays visible (you still need to know you are offline while a
  // form is open), only the affordances step back. See useIsInteractionBlocked.
  const isInteractionBlocked = useIsInteractionBlocked()
  const queryClient = useQueryClient()
  const params = useParams<{ projectStageId?: string; issueTypeId?: string; issueId?: string }>()
  const pageDataUpdatedAt = useOfflineBannerStore(s => s.pageDataUpdatedAt)
  const isOfflineModeEnabled = useOfflineModeStore(s => s.isOfflineModeEnabled)
  const disableOfflineMode = useOfflineModeStore(s => s.disableOfflineMode)
  // Scope to offline-mode-relevant queries only — unscoped useIsFetching() would
  // re-render on every global query state change (prefetch, pagination, Mercure, etc.)
  const fetchingCount = useIsFetching({
    predicate: query => {
      const key = query.queryKey[0]
      return (
        key === 'singleIssue' ||
        key === 'singleIssueComments' ||
        key === 'singleIssueEvents' ||
        key === 'stageIssuesByIssueType' ||
        key === 'currStageIssueTypes' ||
        key === 'currStageUsersQuery' ||
        key === 'stageLocations'
      )
    },
  })

  const storeLastCompletedAt = usePrefetchStore(s => s.lastCompletedAt)
  const persistedLastCompletedAt = useMemo(() => getPersistedLastCompletedAt(), [])
  const timestamp = pageDataUpdatedAt ?? storeLastCompletedAt ?? persistedLastCompletedAt

  const [isViewSyncing, setIsViewSyncing] = useState(false)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const handleOpenCacheSettings = () => {
    setPopoverOpen(false)
    navigate('/settings/offline-cache')
  }

  const handleSyncCurrentView = async () => {
    if (isViewSyncing) return
    setPopoverOpen(false)
    setIsViewSyncing(true)
    try {
      const { projectStageId, issueTypeId, issueId } = params
      const queries: Promise<void>[] = []

      if (issueId) {
        // Single issue view — refetch this specific issue + its comments/events.
        // No refetchType:'none' needed (unlike triggerRefresh.ts) because we target
        // a single issue by exact key, not the entire singleIssue prefix (no OOM risk).
        queries.push(
          queryClient.invalidateQueries({ queryKey: ['singleIssue', { id: issueId }] }),
          queryClient.invalidateQueries({ queryKey: ['singleIssueComments', { id: issueId }] }),
          queryClient.invalidateQueries({ queryKey: ['singleIssueEvents', { id: issueId }] }),
        )
      }

      if (projectStageId && issueTypeId) {
        // Issues list view — refetch the list for this stage+issueType
        queries.push(
          queryClient.invalidateQueries({
            queryKey: ['stageIssuesByIssueType', { projectStageId, issueTypeId }],
          }),
        )
      }

      if (projectStageId) {
        // Shared stage-level data
        queries.push(
          queryClient.invalidateQueries({ queryKey: ['currStageIssueTypes', { projectStageId }] }),
          queryClient.invalidateQueries({ queryKey: ['currStageUsersQuery', { projectStageId }] }),
          queryClient.invalidateQueries({ queryKey: ['stageLocations', { id: projectStageId }] }),
        )
      }

      await Promise.allSettled(queries)
    } finally {
      setIsViewSyncing(false)
    }
  }

  const relativeTime = useRelativeTime(timestamp)

  // Truly offline on native → show offline banner
  const showTrueOffline = !isOnline && isNativePlatform()
  // Online but offline mode enabled → show offline mode indicator
  const showOfflineMode = isOnline && isOfflineModeEnabled

  if (!showTrueOffline && !showOfflineMode) return null

  // Stacks at the very top on mobile (above the topbar) so a wrapping status
  // line doesn't push the back arrow far from the page heading. Desktop keeps
  // its own top stacking because it has no mobile topbar.
  const positionClasses = cn(
    'fixed left-0 right-0 z-[110]',
    'top-[calc(var(--safe-area-inset-top)+var(--top-banner-offset,0px))]',
    'md:top-[var(--top-banner-offset,0px)]',
    'md:left-[var(--sidebar-width)]',
  )

  const timeLabel = relativeTime
    ? pageDataUpdatedAt
      ? t('offline.banner.dataAge', { time: relativeTime })
      : t('offline.banner.lastSync', { time: relativeTime })
    : null

  // True offline — simple amber bar, no actions
  if (showTrueOffline) {
    return (
      <div
        role="alert"
        className={positionClasses}
        style={
          {
            '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
          } as CSSProperties
        }
      >
        <div className="flex h-8 items-center gap-2 border-b border-amber-200 bg-amber-50 px-3 dark:border-amber-900 dark:bg-amber-950/50">
          <WifiOff className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="shrink-0 text-xs font-medium text-amber-800 dark:text-amber-200">
            {t('offline.banner.title')}
          </span>
          {timeLabel && (
            <>
              <span className="shrink-0 text-amber-300 dark:text-amber-700" aria-hidden="true">
                &middot;
              </span>
              <span className="truncate text-xs text-amber-600 dark:text-amber-400">
                {timeLabel}
              </span>
            </>
          )}
        </div>
      </div>
    )
  }

  // Offline mode active (user-enabled, still online) — compact bar + popover actions
  const isUpdating = fetchingCount > 0

  return (
    <div
      role="status"
      className={positionClasses}
      style={
        {
          '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
        } as CSSProperties
      }
    >
      {/* Compact single-line layout. Icon | label · status (truncates
          together) | action buttons. Everything lives in one row so the
          banner stays 1 line tall regardless of how long the relative-time
          string is. */}
      <div className="flex h-8 items-center gap-2 border-b border-blue-200 bg-blue-50 px-3 dark:border-blue-900 dark:bg-blue-950/50">
        <CloudOff className="size-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="shrink-0 text-xs font-medium text-blue-800 dark:text-blue-200">
            {t('offlineMode.banner.active')}
          </span>
          {isUpdating ? (
            <>
              <span className="shrink-0 text-blue-300 dark:text-blue-700" aria-hidden="true">
                &middot;
              </span>
              <RefreshCw className="size-3 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
              <span className="truncate text-xs text-blue-600 dark:text-blue-400">
                {t('offlineMode.banner.updating')}
              </span>
            </>
          ) : (
            timeLabel && (
              <>
                <span className="shrink-0 text-blue-300 dark:text-blue-700" aria-hidden="true">
                  &middot;
                </span>
                <span className="truncate text-xs text-blue-600 dark:text-blue-400">
                  {timeLabel}
                </span>
              </>
            )
          )}
        </div>

        <div className={cn('flex shrink-0 items-center gap-0.5', isInteractionBlocked && 'hidden')}>
          {params.projectStageId && (
            <button
              type="button"
              disabled={isViewSyncing}
              onClick={handleSyncCurrentView}
              className="rounded-md p-1 text-blue-600 hover:bg-blue-100 disabled:opacity-50 dark:text-blue-400 dark:hover:bg-blue-900"
              aria-label={t('offlineMode.banner.syncCurrentView')}
            >
              <RotateCw className={cn('size-3.5', isViewSyncing && 'animate-spin')} />
            </button>
          )}
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="shrink-0 rounded-md p-1 text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900"
                aria-label={t('common.more')}
              >
                <ChevronDown className="size-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={4} className="w-auto min-w-[200px] p-1">
              <button
                type="button"
                onClick={handleOpenCacheSettings}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent"
              >
                <HardDrive className="size-4 shrink-0" />
                {t('offlineMode.banner.manageCache')}
              </button>
              {params.projectStageId && (
                <button
                  type="button"
                  disabled={isViewSyncing}
                  onClick={handleSyncCurrentView}
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent disabled:opacity-50"
                >
                  <RotateCw className={cn('size-4 shrink-0', isViewSyncing && 'animate-spin')} />
                  {t('offlineMode.banner.syncCurrentView')}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setPopoverOpen(false)
                  disableOfflineMode()
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground hover:bg-accent"
              >
                <Power className="size-4 shrink-0" />
                {t('offlineMode.banner.disable')}
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  )
}
